import { type CSSProperties, useRef } from "react";
import { SplitText, gsap, useGSAP } from "../../animations/gsapSetup.ts";
import { useIsMobile } from "../../hooks/useMediaQuery.ts";
import { useReducedMotion } from "../../hooks/useReducedMotion.ts";
import styles from "./AnimatedHeadline.module.css";

interface AnimatedHeadlineProps {
  text: string;
  as?: "h1" | "h2" | "h3";
  className?: string;
  style?: CSSProperties;
  /** "chars" for character-level reveal, "words" for word-level */
  splitType?: "chars" | "words";
}

export function AnimatedHeadline({
  text,
  as: Tag = "h2",
  className,
  style,
  splitType = "words",
}: AnimatedHeadlineProps) {
  const reducedMotion = useReducedMotion();
  const isMobile = useIsMobile();
  const elRef = useRef<HTMLHeadingElement>(null);

  // On mobile, downgrade chars to words for GPU performance
  const effectiveSplitType = isMobile && splitType === "chars" ? "words" : splitType;

  useGSAP(
    () => {
      const el = elRef.current;
      if (!el) return;

      if (reducedMotion) {
        gsap.set(el, { autoAlpha: 1 });
        return;
      }

      const split = SplitText.create(el, { type: effectiveSplitType });
      const targets = split[effectiveSplitType] ?? [];

      gsap.set(el, { autoAlpha: 1 });

      gsap.from(targets, {
        y: effectiveSplitType === "chars" ? "100%" : 40,
        opacity: 0,
        filter: effectiveSplitType === "chars" ? "blur(4px)" : "none",
        duration: effectiveSplitType === "chars" ? 0.6 : 0.8,
        stagger: effectiveSplitType === "chars" ? 0.03 : 0.06,
        ease: "power4.out",
        scrollTrigger: {
          trigger: el,
          start: "top 85%",
          toggleActions: "play none none reverse",
        },
        onComplete: () => {
          for (const t of targets) {
            if (t instanceof HTMLElement) {
              t.style.filter = "";
            }
          }
        },
      });
    },
    {
      scope: elRef,
      // 言語切替時の再アニメーション:
      // text(翻訳テキスト)がdependenciesに入っているため、
      // 言語変更 → text変更 → useGSAPが旧コンテキストをrevert(SplitText DOM復元)
      // → 新テキストで再split → 再アニメーション。
      // 旧実装はcallback refの一度きり初期化でこれができなかった。
      dependencies: [text, effectiveSplitType, reducedMotion, isMobile],
    },
  );

  return (
    <Tag
      ref={elRef}
      className={`${styles.headline} ${className ?? ""}`}
      style={{ ...style, visibility: "hidden" }}
    >
      {text}
    </Tag>
  );
}
